import { DocumentType } from './DocumentType';

/**
 * Locador do Contrato de Locação (ENG-568). Espelho das regras do back
 * (Api.Almada/Features/Cases/Lessor/LessorDocumentRules.cs) — quem manda é o servidor; aqui é UX.
 */

/** Espelho de Context.Almada/Enums/LessorPersonType.cs (valores explícitos, persistidos). */
export enum LessorPersonType {
	Individual = 1,
	LegalEntity = 2,
}

/** Espelho de Context.Almada/Enums/LessorIdentityType.cs. */
export enum LessorIdentityType {
	Cnh = 1,
	Cin = 2,
	Rg = 3,
}

/** Espelho de Context.Almada/Enums/LessorRepresentationAct.cs (valores explícitos, persistidos). */
export enum LessorRepresentationAct {
	SocialContract = 1,
	BylawsAndElectionMinutes = 2,
}

/** As duas opções do campo "ato" da procuração PJ do Jurídico (20/10/2026), com as palavras dela. */
export const LESSOR_REPRESENTATION_ACT_OPTIONS = [
	{
		label: 'Contrato social / última alteração consolidada',
		value: LessorRepresentationAct.SocialContract,
	},
	{ label: 'Estatuto e ata de eleição', value: LessorRepresentationAct.BylawsAndElectionMinutes },
];

export const LESSOR_PERSON_TYPE_OPTIONS = [
	{ label: 'Pessoa física', value: LessorPersonType.Individual },
	{ label: 'Pessoa jurídica', value: LessorPersonType.LegalEntity },
];

/** CNH e CIN preferenciais; RG aceito (DEC-3). A qualidade é conferência humana, nunca bloqueia. */
export const LESSOR_IDENTITY_TYPE_OPTIONS = [
	{ label: 'CNH', value: LessorIdentityType.Cnh },
	{ label: 'CIN', value: LessorIdentityType.Cin },
	{ label: 'RG', value: LessorIdentityType.Rg },
];

/**
 * Tipos dedicados ao Locador (DEC-16). Allow-list PRÓPRIA, separada de `occurrenceDocsOptions`
 * do wizard: o mesmo "Contrato social" com dono diferente seria indistinguível na conferência.
 */
export const LESSOR_DOCUMENT_TYPES: readonly DocumentType[] = [
	DocumentType.LessorIdentityDocument,
	DocumentType.LessorProofOfAddress,
	DocumentType.LessorSocialContract,
	DocumentType.LessorResponsibleIdentityDocument,
	DocumentType.LessorResponsibleProofOfAddress,
];

/**
 * PF: identidade com foto + comprovante de residência. PJ: contrato social + identidade com foto
 * do responsável + comprovante de endereço do responsável (DEC-21, DEC-35, DEC-36).
 */
export function requiredLessorDocumentTypes(personType: LessorPersonType | null | undefined): DocumentType[] {
	switch (personType) {
		case LessorPersonType.Individual:
			return [DocumentType.LessorIdentityDocument, DocumentType.LessorProofOfAddress];
		case LessorPersonType.LegalEntity:
			return [
				DocumentType.LessorSocialContract,
				DocumentType.LessorResponsibleIdentityDocument,
				DocumentType.LessorResponsibleProofOfAddress,
			];
		default:
			return [];
	}
}

/**
 * Rótulos dos tipos do Locador para dicas e para a mensagem ao parceiro. Espelham o Display name
 * do back (Context.Almada/Enums/DocumentType.cs): quem renderiza listas usa o enum do servidor
 * (`GeneralService.getEnumItems`); este mapa serve onde a lista ainda não chegou.
 */
export const LESSOR_DOCUMENT_TYPE_LABELS: Record<number, string> = {
	[DocumentType.LessorIdentityDocument]: 'Locador — documento de identificação',
	[DocumentType.LessorProofOfAddress]: 'Locador — comprovante de residência',
	[DocumentType.LessorSocialContract]: 'Locador PJ — contrato social',
	[DocumentType.LessorResponsibleIdentityDocument]: 'Locador PJ — identificação do responsável',
	[DocumentType.LessorResponsibleProofOfAddress]: 'Locador PJ — comprovante de endereço do responsável',
};

export function lessorDocumentTypeLabel(type: DocumentType | number): string {
	return LESSOR_DOCUMENT_TYPE_LABELS[type] ?? `Documento ${type}`;
}

export function isLessorDocumentType(type: DocumentType | number | null | undefined): boolean {
	return type != null && LESSOR_DOCUMENT_TYPES.includes(type as DocumentType);
}

/** Texto literal do escopo do solicitante (§8), exibido no topo do bloco. */
export const LESSOR_BLOCK_NOTICE =
	'Informe os dados e anexe os documentos do Locador que consta no Contrato de Locação. ' +
	'Documentos incorretos, ilegíveis ou em nome de outra pessoa podem gerar travas no andamento da ocorrência.';

/** Motivo tipado da pendência documental (DEC-30). */
export const LESSOR_PENDING_REASON = 'Falta de documento do Locador';

/** Limite por arquivo só para os tipos do Locador (DEC-2). O teto do request segue 50 MB. */
export const LESSOR_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Copy B (DEC-7, DEC-30), igual à do back: nomeia os tipos a reenviar. Com o cancelamento no 11º
 * dia, um parceiro que não adivinha qual documento está errado perde o sinistro.
 */
export function buildLessorPendingMessage(documentLabels: string[]): string {
	const list = documentLabels.length > 0 ? documentLabels.join(', ') : 'os documentos do Locador';
	return `${LESSOR_PENDING_REASON}. Reenvie: ${list}. A ocorrência permanece em análise documental até a regularização.`;
}
